import {
	Dispatch,
	FunctionComponent,
	ReactNode,
	SetStateAction,
	createContext,
	useContext,
	useState,
} from 'react';
import moment, { Moment } from 'moment';
import {
	PermissionAddon,
	GeoNode,
	Subscription,
	SubscriptionPlan,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import { SubscribableApplication } from 'domain/subscription/interfaces/SubscribableApplication';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';

export interface SubscriptionFormState {
	startDate: Moment;
	endDate?: Moment;
	seats: number;
	type?: SubscriptionType | null;
	applicationPermissions: ApplicationPermissions[];
	description?: string;
}

export interface ApplicationPermissions {
	application: SubscribableApplication;
	plan: SubscriptionPlan;
	addons: PermissionAddon[];
	countries: GeoNode[];
}

export type SubscriptionFormContextType = {
	subscriptionFormState: SubscriptionFormState;
	setSubscriptionFormState: Dispatch<SetStateAction<SubscriptionFormState>>;
	selectedApplications: string[];
	activeSubscription?: Subscription;
	setActiveSubscription: Dispatch<SetStateAction<Subscription | undefined>>;
	resetFormState: () => void;
};

const defaultFormState = {
	startDate: moment.utc(),
	endDate: undefined,
	seats: 1,
	type: null,
	applicationPermissions: [],
	description: '',
};

export const SubscriptionFormContext =
	createContext<SubscriptionFormContextType | null>(null);

const SubscriptionFormContextProvider: FunctionComponent<{
	children: ReactNode;
}> = ({ children }) => {
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const initialFormState = {
		...defaultFormState,
		type: isRestrictedToRole(AdminRole.SALES)
			? SubscriptionType.Trial
			: null,
	};

	const [subscriptionFormState, setSubscriptionFormState] =
		useState<SubscriptionFormState>(initialFormState);
	const [activeSubscription, setActiveSubscription] =
		useState<Subscription>();

	const selectedApplications =
		subscriptionFormState.applicationPermissions.map(
			(p) => p.application.id,
		);

	const resetFormState = () => {
		setSubscriptionFormState({
			...initialFormState,
			applicationPermissions: [],
		});
	};

	return (
		<SubscriptionFormContext.Provider
			value={{
				subscriptionFormState: subscriptionFormState,
				setSubscriptionFormState: setSubscriptionFormState,
				selectedApplications: selectedApplications,
				activeSubscription: activeSubscription,
				setActiveSubscription: setActiveSubscription,
				resetFormState: resetFormState,
			}}
		>
			{children}
		</SubscriptionFormContext.Provider>
	);
};

export function useSubscriptionFormContext() {
	const context = useContext(SubscriptionFormContext);
	if (!context) {
		throw new Error(
			'useSubscriptionFormContext must be used within the SubscriptionFormContextProvider',
		);
	}
	return context;
}

export default SubscriptionFormContextProvider;
